import JSZip from "jszip";
import { validateDrawioFile } from "./drawioUtils";
import { getDefaultStore } from "jotai";
import { chatbotConfigState } from "../../state/config/ChatbotConfigState";
import { TRANSLATIONS } from "../constants/translations";

import { mapExtensionToAllowedContentType } from "../constants/contentTypes";
import { BUCKET_MEDIA_PATH } from "../constants/sharedConstants";
import { DEFAULT_SUPPORT_NODE_REGEX } from "../constants/parsingConstants";
import { getFileNameWithoutExtension } from "../common/stringUtils";

function getActiveTranslation() {
    try {
        const store = getDefaultStore();
        const cfg = store.get(chatbotConfigState);
        const lang = (cfg?.language || 'de').toLowerCase().startsWith('en') ? 'en' : 'de';
        return TRANSLATIONS[lang] || TRANSLATIONS.de;
    } catch (e) {
        return TRANSLATIONS.de;
    }
}

function fmt(template, vars = {}) {
    return String(template || '').replace(/\{(\w+)\}/g, (_, k) => (vars[k] !== undefined ? String(vars[k]) : ''));
}

const MACOS_ZIP_FILE_PREFIX = "__MACOSX/";
const MACOS_DS_STORE_FILE = ".DS_Store";

export async function validateDrawioZip(file) {
    try {
        const t = getActiveTranslation();
        if (!file) return { errors: [t.zipNoFileSelected || "Keine Datei ausgewählt"] };
        if (!file.name.endsWith('.zip')) return { errors: [t.zipInvalidFileType || "Ungültiger Dateityp"] };

        let zip;
        try {
            zip = await JSZip.loadAsync(file);
        } catch (error) {
            console.error("ZIP load error:", error);
            return { errors: [`Ein unerwarteter Fehler ist aufgetreten: ${error.message}`] };
        }

        const errors = [];
        const media = [];
        const textFileData = [];
        const domainIdentifiersWithoutMedia = [];
        let hasDrawioFile = false;
        let drawioContent = null;

        // Step 1: Find and read the main .drawio file
        for (const [fullPath, zipEntry] of Object.entries(zip.files)) {
            if (zipEntry.dir || fullPath.startsWith(MACOS_ZIP_FILE_PREFIX) || fullPath.endsWith(MACOS_DS_STORE_FILE)) continue;

            if (fullPath.endsWith('.drawio')) {
                if (hasDrawioFile) {
                    errors.push(t.zipMultipleDrawioFiles || "Mehrere .drawio Dateien gefunden");
                    continue;
                }
                hasDrawioFile = true;
                drawioContent = await zipEntry.async("text");
            }
        }

        if (!hasDrawioFile) {
            return { errors: [t.zipNoDrawioFile || "Keine .drawio Datei im ZIP gefunden"] };
        }

        // Validate the extracted drawio XML
        const { catalog, errors: drawioErrors } = await validateDrawioFile(drawioContent);
        if (drawioErrors?.length > 0) {
            return { errors: drawioErrors };
        }

        // Step 2: Process all media and text files inside the ZIP
        for (const [fullPath, zipEntry] of Object.entries(zip.files)) {
            if (zipEntry.dir || fullPath.startsWith(MACOS_ZIP_FILE_PREFIX) || fullPath.endsWith(MACOS_DS_STORE_FILE)) continue;
            if (fullPath.endsWith('.drawio')) continue; 

            // Robustly find the "media/" directory anywhere in the zip path
            const mediaRegex = new RegExp(`(^|/)${BUCKET_MEDIA_PATH}/`, "i");
            const mediaMatch = fullPath.match(mediaRegex);

            if (mediaMatch) {
                // Extract the path strictly AFTER the "media/" folder
                const pathAfterMedia = fullPath.substring(mediaMatch.index + mediaMatch[0].length);
                const pathParts = pathAfterMedia.split("/");

                // If length is < 2, the file is directly in media/ and missing the Node-ID folder!
                if (pathParts.length < 2) {
                    errors.push(fmt(t.zipInvalidFilePath || "Ungültiger Dateipfad: {path}. Die Datei muss in einem Knoten-ID-Ordner liegen (z.B. media/KnotenID/bild.jpg)", { path: fullPath }));
                    continue;
                }

                const domainNodeIdentifier = pathParts[0]; // The Node-ID folder
                const fileName = pathParts.slice(1).join("/"); // The actual file name
                const fileExtension = fileName.split(".").pop()?.toLowerCase();

                if (!fileExtension) {
                    errors.push(fmt(t.zipNoValidFileType || "Kein gültiger Dateityp: {file}", { file: fullPath }));
                    continue;
                }

                if (fileExtension === "txt") {
                    const textContent = await zipEntry.async("text");
                    textFileData.push({
                        domainIdentifier: domainNodeIdentifier,
                        content: textContent,
                        fileName: fileName,
                    });
                } else {
                    const validatedContentType = mapExtensionToAllowedContentType(fileExtension);
                    if (validatedContentType) {
                        const blobContent = await zipEntry.async("blob"); 
                        media.push({
                            fileName: fileName,
                            domainNodeIdentifier: domainNodeIdentifier,
                            bytes: blobContent, 
                        });
                    } else {
                        // Explicit fallback if the file type inside the node folder is not allowed
                        errors.push(fmt(t.zipNoValidFileType || "Kein gültiger Dateityp: {file}", { file: fullPath }));
                    }
                }
            } else {
                // Handle files outside the media folder
                const pathParts = fullPath.split("/");
                if (pathParts.length > 1) {
                    const possibleNodeId = pathParts[pathParts.length - 2];
                    if (possibleNodeId && !DEFAULT_SUPPORT_NODE_REGEX.test(possibleNodeId)) {
                        const hasOtherEntries = Object.keys(zip.files).some(key => key.includes(`/${possibleNodeId}/`) && key !== fullPath);
                        if (!hasOtherEntries && !domainIdentifiersWithoutMedia.includes(possibleNodeId)) {
                            domainIdentifiersWithoutMedia.push(possibleNodeId);
                        }
                    }
                }
            }
        }

        if (errors.length > 0) return { errors };

        // Attach companion text files to their media before upload. Previously
        // textFileData was returned but never persisted, so image descriptions
        // could not participate in chat responses or media previews.
        const descriptionsByMediaKey = new Map();
        const descriptionsByDomain = new Map();
        textFileData.forEach((entry) => {
            const basename = getFileNameWithoutExtension(entry.fileName || '').toLowerCase();
            descriptionsByMediaKey.set(`${entry.domainIdentifier}/${basename}`, entry.content);
            if (!descriptionsByDomain.has(entry.domainIdentifier)) {
                descriptionsByDomain.set(entry.domainIdentifier, entry.content);
            }
        });

        media.forEach((entry) => {
            const basename = getFileNameWithoutExtension(entry.fileName || '').toLowerCase();
            entry.description = descriptionsByMediaKey.get(`${entry.domainNodeIdentifier}/${basename}`)
                || descriptionsByDomain.get(entry.domainNodeIdentifier)
                || undefined;
        });

        return { mediaFiles: media, textFileData, domainIdentifiersWithoutMedia, catalog };

    } catch (error) {
        console.error("ZIP Processing Error:", error);
        return { errors: [`Ein unerwarteter Fehler ist aufgetreten: ${error.message}`] };
    }
}
